// frontend/src/services/storage.ts
// Servicio de almacenamiento local con IndexedDB para soporte offline

import type { Cadete, RegistroPresentismo } from '../lib/api';

const DB_NAME = 'sgcc_db';
const DB_VERSION = 12;

interface StoredCadete extends Cadete {
  synced_at?: string;
  pending_sync?: boolean;
}

export interface StoredRegistro extends RegistroPresentismo {
  pending_sync?: boolean;
  local_id?: string;
  // Tombstone: registro desmarcado localmente, pendiente de borrar en el servidor.
  eliminado?: boolean;
  // Versión local: se incrementa en cada escritura local pendiente. Permite
  // marcar como sincronizada solo la versión que efectivamente se subió.
  rev?: number;
  // Rechazado definitivamente por el servidor (ej. 'Sin permiso', estado
  // inválido, cadete inexistente). Ya no está pendiente (pending_sync: false)
  // pero se conserva localmente para no perder la marca en silencio.
  sync_rechazado?: boolean;
  sync_error?: string;
  // Hora (ISO, reloj del dispositivo) en que se confirmó la subida al servidor.
  synced_at?: string;
}

// Referencia a un registro enviado al servidor (local_id + versión enviada)
export interface RegistroEnviado {
  local_id: string;
  rev?: number;
}

// Registro rechazado definitivamente por el servidor
export interface RegistroRechazado extends RegistroEnviado {
  error?: string;
}

// Ventana de fechas que el servidor envió completa en un pull. `snapshotAt` es
// la hora del SERVIDOR del snapshot: los registros sincronizados después de esa
// hora no se borran ni se pisan (el snapshot puede ser anterior a la subida).
export interface VentanaCompleta {
  desde: string;
  hasta: string;
  snapshotAt?: string;
}

// Nombre de la caché runtime del Service Worker para /api/ (versiones previas
// la usaban con NetworkFirst; ahora se limpia en logout / clearAll).
export const API_CACHE_NAME = 'sgcc-api-cache';

// Los tombstones no se muestran en la UI ni cuentan para reportes.
const esVisible = (r: StoredRegistro) => r.eliminado !== true;

interface SyncMetadata {
  cliente_id: string;
  ultima_sincronizacion: string | null;
  registros_pendientes: number;
}

class StorageService {
  private db: IDBDatabase | null = null;
  private lastRev = 0;

  // Versión monotónica para escrituras locales (ms + desempate).
  private nextRev(): number {
    this.lastRev = Math.max(Date.now(), this.lastRev + 1);
    return this.lastRev;
  }

  private notifySyncDataChanged(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
    }
  }

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Error al abrir IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB inicializado correctamente');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store para cadetes
        if (!db.objectStoreNames.contains('cadetes')) {
          const cadetesStore = db.createObjectStore('cadetes', { keyPath: 'id' });
          cadetesStore.createIndex('dni', 'dni', { unique: true });
          cadetesStore.createIndex('apellido', 'apellido');
        } else {
          const tx = (event as IDBVersionChangeEvent & { target: IDBOpenDBRequest }).target.transaction!;
          const cadetesStore = tx.objectStore('cadetes');
          if (cadetesStore.indexNames.contains('numero')) {
            cadetesStore.deleteIndex('numero');
          }
        }

        // Store para registros
        if (!db.objectStoreNames.contains('registros')) {
          const registrosStore = db.createObjectStore('registros', { keyPath: 'local_id' });
          registrosStore.createIndex('cadete_id', 'cadete_id');
          registrosStore.createIndex('fecha', 'fecha');
          registrosStore.createIndex('estado', 'estado');
          registrosStore.createIndex('pending_sync', 'pending_sync');
        }

        // Store para metadata de sincronización
        if (!db.objectStoreNames.contains('sync_metadata')) {
          db.createObjectStore('sync_metadata', { keyPath: 'cliente_id' });
        }

        // Store para cola de sincronización
        if (!db.objectStoreNames.contains('sync_queue')) {
          const syncQueueStore = db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
          syncQueueStore.createIndex('created_at', 'created_at');
        }

        // Store para tipos_licencias
        if (!db.objectStoreNames.contains('tipos_licencias')) {
          const tiposLicenciasStore = db.createObjectStore('tipos_licencias', { keyPath: 'id' });
          tiposLicenciasStore.createIndex('codigo', 'codigo', { unique: true });
        }

        // Store para licencias
        if (!db.objectStoreNames.contains('licencias')) {
          const licenciasStore = db.createObjectStore('licencias', { keyPath: 'id' });
          licenciasStore.createIndex('cadete_id', 'cadete_id');
          licenciasStore.createIndex('fecha_inicio', 'fecha_inicio');
          licenciasStore.createIndex('fecha_fin', 'fecha_fin');
        }
      };
    });
  }

  // ============================================
  // CADETES
  // ============================================
  async setCadetes(cadetes: Cadete[]): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readwrite');
      const store = transaction.objectStore('cadetes');
      const synced_at = new Date().toISOString();

      // No pisar ediciones locales pendientes de sincronizar.
      for (const cadete of cadetes) {
        const getReq = store.get(cadete.id);
        getReq.onsuccess = () => {
          const local: StoredCadete | undefined = getReq.result;
          if (local && local.pending_sync === true) return;
          const stored: StoredCadete = { ...cadete, synced_at, pending_sync: false };
          store.put(stored);
        };
        getReq.onerror = () => reject(getReq.error);
      }

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCadetes(): Promise<StoredCadete[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readonly');
      const store = transaction.objectStore('cadetes');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getCadeteByDNI(dni: string): Promise<StoredCadete | undefined> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readonly');
      const index = transaction.objectStore('cadetes').index('dni');
      const request = index.get(dni);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async searchCadetes(termino: string): Promise<StoredCadete[]> {
    if (!this.db) await this.init();

    const cadetes = await this.getCadetes();
    const terminoLower = termino.toLowerCase();

    return cadetes.filter(
      (c) =>
        c.apellido.toLowerCase().includes(terminoLower) ||
        c.nombres.toLowerCase().includes(terminoLower) ||
        c.dni.includes(termino)
    );
  }

  // ============================================
  // REGISTROS
  // ============================================
  async saveRegistro(registro: RegistroPresentismo, pendingSync = true): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');

      const storedRegistro: StoredRegistro = {
        ...registro,
        pending_sync: pendingSync,
        // Un solo registro por cadete y fecha: nuevas marcas reemplazan la anterior.
        local_id: registro.local_id || `${registro.cadete_id}-${registro.fecha}`,
        // Una nueva marca reemplaza a un tombstone previo.
        eliminado: false,
        rev: this.nextRev(),
        // Una nueva marca reemplaza a un rechazo previo.
        sync_rechazado: false,
        sync_error: undefined,
      };

      store.put(storedRegistro);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Guardado masivo: una sola transacción y un único evento de cambio,
  // en lugar de N transacciones/eventos al hidratar desde el servidor.
  async saveRegistros(registros: RegistroPresentismo[], pendingSync = true): Promise<void> {
    if (!this.db) await this.init();
    if (registros.length === 0) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');

      for (const registro of registros) {
        const storedRegistro: StoredRegistro = {
          ...registro,
          pending_sync: pendingSync,
          local_id: registro.local_id || `${registro.cadete_id}-${registro.fecha}`,
          eliminado: false,
          rev: this.nextRev(),
          sync_rechazado: false,
          sync_error: undefined,
        };
        store.put(storedRegistro);
      }

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getRegistros(): Promise<StoredRegistro[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readonly');
      const store = transaction.objectStore('registros');
      const request = store.getAll();

      request.onsuccess = () => resolve((request.result as StoredRegistro[]).filter(esVisible));
      request.onerror = () => reject(request.error);
    });
  }

  async getRegistrosByFecha(fecha: string): Promise<StoredRegistro[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readonly');
      const index = transaction.objectStore('registros').index('fecha');
      const request = index.getAll(fecha);

      request.onsuccess = () => resolve((request.result as StoredRegistro[]).filter(esVisible));
      request.onerror = () => reject(request.error);
    });
  }

  async getRegistroByCadeteAndFecha(cadeteId: number, fecha: string): Promise<StoredRegistro | undefined> {
    if (!this.db) await this.init();

    const registros = await this.getRegistrosByFecha(fecha);
    return registros.find((r) => r.cadete_id === cadeteId);
  }

  async getRegistrosByCadete(cadeteId: number): Promise<StoredRegistro[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readonly');
      const index = transaction.objectStore('registros').index('cadete_id');
      const request = index.getAll(cadeteId);

      request.onsuccess = () => resolve((request.result as StoredRegistro[]).filter(esVisible));
      request.onerror = () => reject(request.error);
    });
  }

  // Borrado local "duro" (no se propaga al servidor). Para desmarcar un
  // presentismo usar desmarcarRegistro(), que sí sincroniza el borrado.
  async deleteRegistrosByCadeteAndFecha(cadeteId: number, fecha: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');
      const request = store.index('fecha').getAll(fecha);

      request.onsuccess = () => {
        for (const registro of request.result as StoredRegistro[]) {
          if (registro.cadete_id === cadeteId && registro.local_id) {
            store.delete(registro.local_id);
          }
        }
      };
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Desmarca el presentismo de un cadete en una fecha: escribe un tombstone
  // pendiente de sincronizar (eliminado: true). Al sincronizarse con éxito el
  // tombstone se borra localmente (ver markRegistrosAsSynced).
  async desmarcarRegistro(cadeteId: number, fecha: string): Promise<void> {
    if (!this.db) await this.init();

    const local_id = `${cadeteId}-${fecha}`;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');
      const getReq = store.get(local_id);

      getReq.onsuccess = () => {
        const existing: StoredRegistro | undefined = getReq.result;
        const tombstone: StoredRegistro = {
          cadete_id: cadeteId,
          fecha,
          hora_local: existing?.hora_local ?? '',
          estado: existing?.estado ?? 'presente',
          local_id,
          eliminado: true,
          pending_sync: true,
          rev: this.nextRev(),
        };
        store.put(tombstone);
      };
      getReq.onerror = () => reject(getReq.error);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getPendingRegistros(): Promise<StoredRegistro[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readonly');
      const store = transaction.objectStore('registros');
      const request = store.getAll();

      request.onsuccess = () => {
        const allRegistros = request.result as StoredRegistro[];
        const pending = allRegistros.filter(r => r.pending_sync === true);
        resolve(pending);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Marca como sincronizados los registros enviados. Solo se marca si la
  // versión local (rev) sigue siendo la que se envió: si el usuario modificó el
  // registro mientras se subía, la nueva versión queda pendiente. Los
  // tombstones sincronizados se eliminan localmente.
  // `serverTimestamp`: hora del SERVIDOR de la subida (data.timestamp de la
  // respuesta); se guarda como synced_at para compararla con el snapshotAt de
  // un pull sin depender del reloj del dispositivo. Sin ella, se usa la hora local.
  async markRegistrosAsSynced(
    enviados: Array<string | RegistroEnviado>,
    serverTimestamp?: string
  ): Promise<void> {
    if (!this.db) await this.init();
    if (enviados.length === 0) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');
      const syncedAt =
        serverTimestamp && Number.isFinite(Date.parse(serverTimestamp))
          ? serverTimestamp
          : new Date().toISOString();

      for (const enviado of enviados) {
        const ref: RegistroEnviado = typeof enviado === 'string' ? { local_id: enviado } : enviado;
        const verificarRev = typeof enviado !== 'string';
        const getRequest = store.get(ref.local_id);
        getRequest.onsuccess = () => {
          const registro: StoredRegistro | undefined = getRequest.result;
          if (!registro) return;
          // Cambió durante la subida: dejarlo pendiente para la próxima sync.
          if (verificarRev && registro.rev !== ref.rev) return;

          if (registro.eliminado === true) {
            store.delete(ref.local_id);
            return;
          }
          registro.pending_sync = false;
          registro.synced_at = syncedAt;
          registro.sync_rechazado = false;
          registro.sync_error = undefined;
          store.put(registro);
        };
        getRequest.onerror = () => reject(getRequest.error);
      }

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Marca registros como rechazados definitivamente por el servidor: dejan de
  // estar pendientes (no bloquean la sync ni la actualización de la app) pero
  // se conservan para que el usuario pueda verlos/descartarlos. Igual que en
  // markRegistrosAsSynced, solo si la versión local sigue siendo la enviada.
  // Un tombstone rechazado se elimina (el desmarcado no pudo aplicarse; el
  // próximo pull trae el estado del servidor).
  async marcarRegistrosRechazados(rechazados: RegistroRechazado[]): Promise<void> {
    if (!this.db) await this.init();
    if (rechazados.length === 0) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');

      for (const ref of rechazados) {
        const getRequest = store.get(ref.local_id);
        getRequest.onsuccess = () => {
          const registro: StoredRegistro | undefined = getRequest.result;
          if (!registro) return;
          if (registro.rev !== ref.rev) return;

          if (registro.eliminado === true) {
            store.delete(ref.local_id);
            return;
          }
          registro.pending_sync = false;
          registro.sync_rechazado = true;
          registro.sync_error = ref.error || 'Rechazado por el servidor';
          store.put(registro);
        };
        getRequest.onerror = () => reject(getRequest.error);
      }

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getRegistrosRechazados(): Promise<StoredRegistro[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readonly');
      const request = transaction.objectStore('registros').getAll();

      request.onsuccess = () =>
        resolve((request.result as StoredRegistro[]).filter((r) => r.sync_rechazado === true));
      request.onerror = () => reject(request.error);
    });
  }

  // Elimina localmente los registros rechazados por el servidor (el próximo
  // pull trae el estado real del servidor para esos cadetes/fechas).
  async descartarRegistrosRechazados(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');
      const request = store.getAll();

      request.onsuccess = () => {
        for (const registro of request.result as StoredRegistro[]) {
          if (registro.sync_rechazado === true && registro.pending_sync !== true && registro.local_id) {
            store.delete(registro.local_id);
          }
        }
      };
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ============================================
  // SINCRONIZACIÓN
  // ============================================
  async getSyncMetadata(): Promise<SyncMetadata | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_metadata'], 'readonly');
      const store = transaction.objectStore('sync_metadata');
      const request = store.get('default');

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async saveSyncMetadata(metadata: SyncMetadata): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_metadata'], 'readwrite');
      const store = transaction.objectStore('sync_metadata');

      store.put({ ...metadata, cliente_id: 'default' });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async clearSyncMetadata(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_metadata'], 'readwrite');
      const store = transaction.objectStore('sync_metadata');
      store.delete('default');

      // Resolver al confirmarse la transacción, no al éxito del request.
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  // Guarda la hora de última sincronización. Debe recibir la hora del
  // SERVIDOR (data.timestamp), no la del dispositivo, ya que se usa como
  // cursor incremental (updatedDesde) en el próximo pull.
  async updateUltimaSincronizacion(serverTimestamp: string): Promise<void> {
    const metadata = await this.getSyncMetadata();
    const pendingCount = await this.getPendingRegistros();

    await this.saveSyncMetadata({
      cliente_id: metadata?.cliente_id || this.generateClientId(),
      ultima_sincronizacion: serverTimestamp,
      registros_pendientes: pendingCount.length,
    });
  }

  async getRegistrosPendientesCount(): Promise<number> {
    const pending = await this.getPendingRegistros();
    return pending.length;
  }

  generateClientId(): string {
    let clientId = localStorage.getItem('sgcc_cliente_id');
    if (!clientId) {
      clientId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('sgcc_cliente_id', clientId);
    }
    return clientId;
  }

  // ============================================
  // LIMPIEZA
  // ============================================
  async cleanupOldSyncedRegistros(diasLimit = 30): Promise<number> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');
      const request = store.getAll();

      request.onsuccess = () => {
        const registros = request.result as StoredRegistro[];
        const now = new Date();
        let deleteCount = 0;

        registros.forEach((registro) => {
          // Los rechazados se conservan hasta que el usuario los descarte.
          if (registro.pending_sync === false && registro.sync_rechazado !== true) {
            // Calcular diferencia en días
            const registroFecha = new Date(registro.fecha + 'T00:00:00');
            const diffTime = Math.abs(now.getTime() - registroFecha.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays > diasLimit && registro.local_id) {
              store.delete(registro.local_id);
              deleteCount++;
            }
          }
        });

        transaction.oncomplete = () => {
          if (deleteCount > 0) {
            console.log(`🧹 Limpieza de IndexedDB: se eliminaron ${deleteCount} registros sincronizados de más de ${diasLimit} días.`);
            this.notifySyncDataChanged();
          }
          resolve(deleteCount);
        };
      };

      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ============================================
  // TIPOS DE LICENCIAS (OFFLINE)
  // ============================================
  async setTiposLicencias(tipos: any[]): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['tipos_licencias'], 'readwrite');
      const store = transaction.objectStore('tipos_licencias');
      
      store.clear();

      tipos.forEach((tipo) => {
        store.put(tipo);
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getTiposLicencias(): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['tipos_licencias'], 'readonly');
      const store = transaction.objectStore('tipos_licencias');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================
  // LICENCIAS DE CADETES (OFFLINE)
  // ============================================
  async setLicencias(licencias: any[]): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['licencias'], 'readwrite');
      const store = transaction.objectStore('licencias');
      
      store.clear();

      licencias.forEach((licencia) => {
        store.put(licencia);
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getLicencias(): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['licencias'], 'readonly');
      const store = transaction.objectStore('licencias');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getLicenciaCadeteActiva(cadeteId: number, fecha: string): Promise<any | null> {
    const licencias = await this.getLicencias();
    const active = licencias.find(l =>
      l.cadete_id === cadeteId &&
      l.fecha_inicio <= fecha &&
      (!l.fecha_fin || l.fecha_fin >= fecha)
    );
    return active || null;
  }

  // ============================================
  // COLA GENÉRICA DE OPERACIONES (sync_queue)
  // ============================================
  async enqueueOp(op: { type: string; payload: any }): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_queue'], 'readwrite');
      const store = transaction.objectStore('sync_queue');

      store.put({ ...op, created_at: new Date().toISOString() });

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getQueue(): Promise<Array<{ id: number; type: string; payload: any; created_at: string }>> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_queue'], 'readonly');
      const store = transaction.objectStore('sync_queue');
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result as Array<{ id: number; type: string; payload: any; created_at: string }>;
        items.sort((a, b) => a.created_at.localeCompare(b.created_at));
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteQueueItem(id: number): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_queue'], 'readwrite');
      const store = transaction.objectStore('sync_queue');

      store.delete(id);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ============================================
  // CADETES — OFFLINE-FIRST
  // ============================================
  async saveCadeteLocal(cadete: Cadete): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readwrite');
      const store = transaction.objectStore('cadetes');

      const stored: StoredCadete = { ...cadete, pending_sync: true, synced_at: undefined };
      store.put(stored);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Merge no destructivo de cadetes del servidor: respeta ediciones locales
  // con pending_sync === true. Con `completo: true` la lista del servidor es
  // la lista COMPLETA de cadetes activos del alcance del usuario, por lo que se
  // eliminan los cadetes locales no pendientes que no vinieron (dados de baja,
  // cambiados de compañía, fuera del alcance).
  async mergeServerCadetes(serverCadetes: Cadete[], opts?: { completo?: boolean }): Promise<void> {
    if (!this.db) await this.init();
    const completo = opts?.completo === true;
    if (serverCadetes.length === 0 && !completo) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readwrite');
      const store = transaction.objectStore('cadetes');
      const synced_at = new Date().toISOString();
      const getAllReq = store.getAll();

      getAllReq.onsuccess = () => {
        const locales = new Map<number, StoredCadete>();
        for (const local of getAllReq.result as StoredCadete[]) {
          locales.set(local.id, local);
        }

        const recibidos = new Set<number>();
        for (const serverCadete of serverCadetes) {
          recibidos.add(serverCadete.id);
          const local = locales.get(serverCadete.id);

          // Local pendiente gana — se conserva tal cual
          if (local && local.pending_sync === true) continue;

          // El servidor es la fuente de verdad de estos campos; las ediciones
          // locales sin sincronizar ya están protegidas por el guard de
          // pending_sync de arriba. Solo caemos al valor local si el servidor
          // aún no devuelve nada.
          const estado_institucional = serverCadete.estado_institucional ?? local?.estado_institucional;
          const estado_metadata = serverCadete.estado_metadata ?? local?.estado_metadata;

          const merged: StoredCadete = {
            ...serverCadete,
            estado_institucional,
            estado_metadata,
            synced_at,
            pending_sync: false,
          };
          store.put(merged);
        }

        if (completo) {
          for (const [id, local] of locales) {
            if (local.pending_sync !== true && !recibidos.has(id)) {
              store.delete(id);
            }
          }
        }
      };
      getAllReq.onerror = () => reject(getAllReq.error);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Alias público de mergeServerCadetes (mismas opciones).
  async mergeCadetes(cadetes: Cadete[], opts?: { completo?: boolean }): Promise<void> {
    return this.mergeServerCadetes(cadetes, opts);
  }

  async getPendingCadetes(): Promise<StoredCadete[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readonly');
      const store = transaction.objectStore('cadetes');
      const request = store.getAll();

      request.onsuccess = () => {
        const all = request.result as StoredCadete[];
        resolve(all.filter(c => c.pending_sync === true));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async markCadetesAsSynced(ids: number[]): Promise<void> {
    if (!this.db) await this.init();
    if (ids.length === 0) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes'], 'readwrite');
      const store = transaction.objectStore('cadetes');

      for (const id of ids) {
        const getReq = store.get(id);
        getReq.onsuccess = () => {
          const cadete: StoredCadete | undefined = getReq.result;
          if (cadete) {
            cadete.pending_sync = false;
            store.put(cadete);
          }
        };
        getReq.onerror = () => reject(getReq.error);
      }

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ============================================
  // LICENCIAS — OFFLINE-FIRST
  // ============================================
  async saveLicenciaLocal(licencia: any): Promise<string> {
    if (!this.db) await this.init();

    const toStore = { ...licencia, pending_sync: true };
    if (!toStore.id) {
      toStore.id = 'temp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
    }
    const savedId: string = toStore.id;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['licencias'], 'readwrite');
      const store = transaction.objectStore('licencias');

      store.put(toStore);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve(savedId);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async mergeServerLicencias(serverLicencias: any[]): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['licencias'], 'readwrite');
      const store = transaction.objectStore('licencias');

      const getAllReq = store.getAll();
      getAllReq.onsuccess = () => {
        const allLocal: any[] = getAllReq.result;

        // Identify local entries that must be preserved (pending or temp id)
        const pendingLocal = allLocal.filter(
          l => l.pending_sync === true || (typeof l.id === 'string' && l.id.startsWith('temp-'))
        );

        // Clear all non-pending entries: delete those not in pendingLocal
        const pendingIds = new Set(pendingLocal.map(l => l.id));
        for (const local of allLocal) {
          if (!pendingIds.has(local.id)) {
            store.delete(local.id);
          }
        }

        // Write server entries (skip if a pending local entry has the same id)
        for (const serverLicencia of serverLicencias) {
          if (!pendingIds.has(serverLicencia.id)) {
            store.put(serverLicencia);
          }
        }
      };
      getAllReq.onerror = () => reject(getAllReq.error);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getPendingLicencias(): Promise<any[]> {
    if (!this.db) await this.init();
    const licencias = await this.getLicencias();
    return licencias.filter(l => l.pending_sync === true || (typeof l.id === 'string' && l.id.startsWith('temp-')));
  }

  // ============================================
  // REGISTROS — MERGE PROTECTION DURING PULL
  // ============================================
  // Merge no destructivo de registros del servidor: nunca pisa un registro
  // local pendiente (incluye tombstones). Si se pasa `ventanaCompleta`, la
  // respuesta contiene TODOS los registros del servidor para ese rango de
  // fechas, por lo que se eliminan los registros locales ya sincronizados de
  // ese rango que no vinieron (borrados en el servidor) — también cuando
  // `registros` está vacío (el servidor no tiene nada en ese rango).
  // Con `ventanaCompleta.snapshotAt` (hora del servidor del snapshot), los
  // registros subidos DESPUÉS de esa hora no se borran ni se pisan: el
  // snapshot es anterior y no los refleja todavía.
  async saveRegistrosMerge(
    registros: RegistroPresentismo[],
    ventanaCompleta?: VentanaCompleta
  ): Promise<void> {
    if (!this.db) await this.init();
    if (registros.length === 0 && !ventanaCompleta) return;

    const snapshotMs = ventanaCompleta?.snapshotAt ? Date.parse(ventanaCompleta.snapshotAt) : NaN;
    // true si el registro local se confirmó en el servidor después del snapshot
    const posteriorAlSnapshot = (local: StoredRegistro): boolean => {
      if (!Number.isFinite(snapshotMs) || !local.synced_at) return false;
      const syncedMs = Date.parse(local.synced_at);
      return Number.isFinite(syncedMs) && syncedMs > snapshotMs;
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['registros'], 'readwrite');
      const store = transaction.objectStore('registros');
      const getAllReq = store.getAll();

      getAllReq.onsuccess = () => {
        const locales = new Map<string, StoredRegistro>();
        for (const local of getAllReq.result as StoredRegistro[]) {
          if (local.local_id) locales.set(local.local_id, local);
        }

        const recibidos = new Set<string>();
        for (const registro of registros) {
          const local_id = registro.local_id || `${registro.cadete_id}-${registro.fecha}`;
          recibidos.add(local_id);
          const existing = locales.get(local_id);
          if (existing && existing.pending_sync === true) {
            // Local pendiente gana — saltar
            continue;
          }
          if (existing && posteriorAlSnapshot(existing)) {
            // Subido después del snapshot: la versión local es más nueva
            continue;
          }
          const storedRegistro: StoredRegistro = {
            ...registro,
            pending_sync: false,
            eliminado: false,
            local_id,
          };
          store.put(storedRegistro);
        }

        if (ventanaCompleta) {
          for (const [local_id, local] of locales) {
            if (
              local.pending_sync !== true &&
              // Rechazados: se conservan hasta que el usuario los descarte
              local.sync_rechazado !== true &&
              !posteriorAlSnapshot(local) &&
              !recibidos.has(local_id) &&
              local.fecha >= ventanaCompleta.desde &&
              local.fecha <= ventanaCompleta.hasta
            ) {
              store.delete(local_id);
            }
          }
        }
      };
      getAllReq.onerror = () => reject(getAllReq.error);

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ============================================
  // CONTEO TOTAL DE PENDIENTES
  // ============================================
  async getPendingTotalCount(): Promise<number> {
    if (!this.db) await this.init();

    // Gestión ops (cadete edits, licencias, grado masivo) are all represented by
    // a queue entry, so queue.length covers them without double-counting
    // against pendingCadetes/pendingLicencias which would also include them.
    const [pending, queue] = await Promise.all([
      this.getPendingRegistros(),
      this.getQueue(),
    ]);

    const uniqueKeys = new Set(pending.map(r => `${r.cadete_id}-${r.fecha}`));
    return uniqueKeys.size + queue.length;
  }

  async clearAll(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(['cadetes', 'registros', 'sync_metadata', 'sync_queue', 'tipos_licencias', 'licencias'], 'readwrite');

      transaction.objectStore('cadetes').clear();
      transaction.objectStore('registros').clear();
      transaction.objectStore('sync_metadata').clear();
      transaction.objectStore('sync_queue').clear();
      transaction.objectStore('tipos_licencias').clear();
      transaction.objectStore('licencias').clear();

      transaction.oncomplete = () => {
        this.notifySyncDataChanged();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    }).then(() => this.clearApiCache());
  }

  // Elimina la caché runtime de respuestas /api/ del Service Worker (datos
  // autenticados). Llamar en logout. Nunca lanza.
  async clearApiCache(): Promise<void> {
    // Avisar también al Service Worker (best effort)
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
    } catch { /* ignorar */ }

    if (typeof caches === 'undefined') return;
    try {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((key) => key === API_CACHE_NAME || key.includes('api'))
          .map((key) => caches.delete(key))
      );
    } catch (err) {
      console.warn('No se pudo limpiar la caché de API:', err);
    }
  }
}

export const storage = new StorageService();
